<!--
  @Sheet  Aged parchment frame for the hero sheets: masthead (name, vocation, level) + page content.
  Renders as <article>, i.e. lands in the 'main' grid area of _Manage.
  Props: `stat` optional snippet shown left of the level medallion; `warn` message below the masthead.
-->

<script>
	import {view} from "../client.svelte.js";

	let {stat = null, warn = "", children} = $props()

	let level = $derived((view.character.upgrading) ? view.character.upgrLevel : view.character.level)
	let subtitle = $derived([view.character.vocation, view.character.race].filter(Boolean).join(", "))
</script>


<!-------------------------------- HTML -------------------------------------->

<article class="sheet classical paper-aged vignette">
	<div class="page">
		<div class="masthead" data-help='header'>
			<div>
				<h1 class="hero-name">{view.character.name || "Unbenannter Held"}</h1>
				{#if subtitle}<div class="subtitle">{subtitle}</div>{/if}
			</div>
			<div class="mast-right">
				{@render stat?.()}
				<div class="medallion" data-help='level'>
					<span class="medallion-value num">{level ?? ""}</span>
					<span class="medallion-label">Stufe</span>
				</div>
			</div>
		</div>
		<div class="double-rule"></div>

		{#if warn}
			<p class="warn">{warn}</p>
		{/if}

		{@render children()}
	</div>
</article>


<!-------------------------------- STYLES -------------------------------------->

<style lang="sass">
	.sheet
		position: relative
		min-height: 0
		margin: 0 24px 24px
		border: 1px solid var(--color-accent-300)
		border-radius: var(--radius-lg)
		overflow: auto
		scrollbar-width: thin
		scrollbar-color: var(--color-accent-400) transparent

	.page
		min-width: 940px
		padding: 36px 56px 64px

	.masthead
		display: flex
		align-items: flex-end
		justify-content: space-between
		flex-wrap: wrap
		gap: 24px

	.hero-name
		margin-top: 2px
		font-weight: 400
		font-size: 52px
		line-height: 1

	.subtitle
		margin-top: 6px
		font-style: italic
		color: var(--color-neutral-700)

	.mast-right
		display: flex
		align-items: flex-end
		gap: 3em

	.medallion
		width: 78px
		height: 78px
		display: flex
		flex-direction: column
		align-items: center
		justify-content: center
		border: 1px solid var(--color-accent)
		border-radius: 50%
		outline: 1px solid var(--color-accent-300)
		outline-offset: 3px

	.medallion-value
		font-family: var(--font-heading)
		font-size: 34px
		line-height: 1

	.medallion-label
		font-family: var(--font-heading)
		font-size: 10px
		font-weight: 600
		letter-spacing: .14em
		text-transform: uppercase
		color: var(--color-accent-700)

	.double-rule
		height: 4px
		margin: 18px 0 0
		border-top: 1px solid var(--color-accent)
		border-bottom: 1px solid var(--color-accent-300)

	.warn
		margin: -14px 0 22px
		font-style: italic
		color: var(--color-accent-800)

	/* print only the sheet: hide every element that neither is, contains nor lies within the sheet */
	@media print
		@page
			margin: 10mm

		:global(body *:not(:has(.sheet)):not(.sheet):not(.sheet *))
			display: none !important

		// the ancestors (body, main grid, section) are locked to the screen height – release them
		:global(html:has(.sheet)), :global(body:has(.sheet)), :global(body *:has(.sheet))
			display: block !important
			width: auto !important
			height: auto !important
			overflow: visible !important
			margin: 0 !important
			padding: 0 !important
			background: none !important

		.sheet
			zoom: .6		// printing scale 60%
			margin: 0
			overflow: visible
			border: none
			border-radius: 0
			print-color-adjust: exact		// keep the aged paper background
			-webkit-print-color-adjust: exact

		.page
			min-width: 0
			padding: 12mm 14mm

		.sheet :global(.btn-outline)
			display: none
</style>
